import { useQuery } from "@tanstack/react-query";
import { Shield, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface CustomerStatsResponse {
  status: string;
  days: number;
  rows: Array<{
    customer: string;
    total: number;
    answered: number;
    answer_rate: number;
  }>;
  prefix_rate: number;
  total_queries: number;
}

/**
 * 고객사 격리 적중률 KPI
 * - prefix 사용율 (사용자가 [고객사] prefix 붙인 비율)
 * - 고객사별 답변 성공률
 */
export function CustomerIsolationKpi() {
  const { data, isLoading } = useQuery<CustomerStatsResponse>({
    queryKey: ["admin", "customer-stats", 7],
    queryFn: () => api.get("/admin/customer-stats", { days: 7 }),
    refetchInterval: 5 * 60_000,
  });

  if (isLoading || !data) {
    return <Skeleton className="h-32" />;
  }

  // 고객사별 집계 (날짜 합산)
  const byCustomer: Record<string, { total: number; answered: number }> = {};
  for (const r of data.rows) {
    if (!byCustomer[r.customer]) byCustomer[r.customer] = { total: 0, answered: 0 };
    byCustomer[r.customer].total += r.total;
    byCustomer[r.customer].answered += r.answered;
  }
  const customerList = Object.entries(byCustomer)
    .map(([cust, v]) => ({
      customer: cust,
      total: v.total,
      answered: v.answered,
      answer_rate: v.total > 0 ? Math.round((v.answered / v.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);

  const prefixIsHigh = data.prefix_rate >= 60;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold">고객사 격리 현황 (최근 7일)</h3>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="p-2 rounded border border-gray-200">
            <div className="text-xs text-gray-500">총 질문 수</div>
            <div className="text-xl font-semibold">{data.total_queries.toLocaleString()}</div>
          </div>
          <div className="p-2 rounded border border-gray-200">
            <div className="text-xs text-gray-500 flex items-center gap-1">
              prefix 사용율
              {prefixIsHigh ? (
                <CheckCircle2 className="h-3 w-3 text-green-600" />
              ) : (
                <AlertTriangle className="h-3 w-3 text-orange-500" />
              )}
            </div>
            <div className="text-xl font-semibold">{data.prefix_rate}%</div>
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-xs text-gray-500 mb-1">고객사별 답변 성공률</div>
          {customerList.slice(0, 5).map((c) => (
            <div key={c.customer} className="flex items-center justify-between text-xs">
              <span className="truncate font-medium">{c.customer}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-gray-500">{c.total}건</span>
                <span
                  className={
                    c.answer_rate >= 70
                      ? "text-green-600 font-semibold"
                      : c.answer_rate >= 50
                      ? "text-yellow-600 font-semibold"
                      : "text-red-600 font-semibold"
                  }
                >
                  {c.answer_rate}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {!prefixIsHigh && (
          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
            prefix 사용율이 낮습니다. 사용자에게 <code>[고객사명]</code> 사용 안내 권장.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
